import { cn } from '@atlas/ui-primitives'
import { FolderPlus, SearchX } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Props for the {@link EmptyState} component.
 *
 * @property title - What is absent, stated plainly ("No users yet")
 * @property description - Optional second line: why it is empty, or what to do next
 * @property variant - Whether the collection is empty at rest or empty because a filter excluded everything
 * @property action - Optional control, typically the button that fills the collection
 * @property className - Optional additional Tailwind classes
 */
export interface EmptyStateProps {
	title: ReactNode
	description?: ReactNode
	variant?: 'first-run' | 'filtered'
	action?: ReactNode
	className?: string
}

/** Each variant's icon and border tone, so the two cases are distinguishable before the copy is read. */
const VARIANT_STYLES = {
	'first-run': { Glyph: FolderPlus, border: 'border-ds-border' },
	filtered: { Glyph: SearchX, border: 'border-ds-border-strong' },
} as const

/**
 * The placeholder a collection shows when it holds nothing.
 *
 * @remarks
 * - **`variant` is the distinction that matters.** `first-run` means the collection
 *   is genuinely empty and the next move is to create something; `filtered` means
 *   rows exist but the current query excludes them, and the next move is to widen
 *   it. Telling a user "no results" when they have simply over-filtered sends them
 *   looking for the wrong problem, so the two carry a different glyph and border
 *   weight rather than differing by copy alone. It also surfaces as
 *   `data-empty-variant` so a test can assert which case rendered.
 * - **Dashed rather than solid border**, so an empty collection reads as a slot
 *   waiting to be filled rather than a card that failed to load.
 * - The glyph is decorative: `title` already states what is absent, so announcing
 *   the icon too would only repeat it to a screen reader.
 *
 * @example
 * ```tsx
 * <EmptyState title="No users yet" description="Invite someone to get started." action={<Button>Invite</Button>} />
 * ```
 *
 * @see packages/ui-components/src/empty-state/empty-state.stories.tsx for visual examples
 */
export function EmptyState({ title, description, variant = 'first-run', action, className }: EmptyStateProps) {
	const { Glyph, border } = VARIANT_STYLES[variant]
	return (
		<div
			data-empty-variant={variant}
			className={cn(
				'rounded-ds-md border border-dashed p-6 text-center text-ds-sm text-ds-text-muted',
				border,
				className,
			)}
		>
			<Glyph aria-hidden="true" className="mx-auto mb-2 h-5 w-5 text-ds-text-subtle" />
			<p className="font-medium text-ds-text">{title}</p>
			{description ? <p className="mt-1">{description}</p> : null}
			{action ? <div className="mt-3">{action}</div> : null}
		</div>
	)
}
